/**
 * The shape of the app: three destinations, each with its own sub-tabs.
 *
 * Declarative rather than a switch statement, because the nav is two levels and
 * the design is specific about both the grouping and the order. The order here
 * is load-bearing. Do not sort it.
 *
 * ## Marketing swallowed Newsletter and Email automation, 12 Aug
 *
 * They were sub-tabs of each product. The prototype now lists eight entries
 * under Marketing — Publishing plan, Channel stats, Inbox, Events, Ambassadors,
 * Partners, Newsletter, Email automation — and switches product *inside* the
 * screen with `marketingNewsletterIsApp` / `...IsPro`. The per-product regions
 * are gone from the prototype entirely.
 *
 * This follows the prototype over that bundle's README, which still describes
 * the old shape. The prototype is the only one of the two that knows about
 * Inbox and Partners, so it is plainly the fuller document — and the README's
 * four-item Marketing list would have deleted two screens that exist. Raised
 * with the designer in `designfeedback.md`; if the README turns out to be the
 * intent, this file and `App.tsx` are the whole change.
 *
 * 21 Oct 2026: Inbox came off, never taken into use, and Competitors — the
 * weekly competitor analysis from Google Drive — went in after Channel stats.
 *
 * That also settles `Partners`, which we added ourselves last round and marked
 * "not in the design". It is in the design now, in the position we put it.
 *
 * ## Internal arrived 13 Aug
 *
 * A fourth destination for the team's own work rather than a customer's: Tasks,
 * Team and Ownership.
 *
 * Team is the editor that already lived under Admin. It is the same component;
 * only its address changed. Pipeline stages and email templates stay under
 * Admin, which the design does not place anywhere.
 *
 * Ownership is the share register, and it is `adminOnly` — as in the design,
 * where the tab does not exist for a member. The rules enforce it; this only
 * keeps a tab out of the row that would answer with a refusal.
 */

export type Destination = 'pro' | 'app' | 'marketing' | 'internal'

export type Screen =
  // Skida PRO
  | 'pro.crm'
  | 'pro.dashboard'
  // Skida App
  | 'app.dashboard'
  // Marketing
  | 'marketing.plan'
  | 'marketing.stats'
  | 'marketing.competitors'
  | 'marketing.events'
  | 'marketing.ambassadors'
  | 'marketing.partners'
  | 'marketing.newsletter'
  | 'marketing.automation'
  // Internal — our own work, not a customer's
  | 'internal.team'
  | 'internal.ownership'
  // Reachable, but not a nav item.
  | 'admin'

/**
 * Screens that used to exist, and where they went.
 *
 * Newsletter and Email automation were sub-tabs of each product until the
 * 12 Aug handoff moved them into Marketing behind a product switch. Four URLs
 * stopped existing on that day, and people have them open in tabs and bookmarks.
 *
 * The product is carried across rather than dropped: somebody who bookmarked
 * the PRO newsletter should land on the newsletter with skida.pro selected, not
 * on skida.app because that happens to be the default.
 */
export const MOVED_SCREENS: Record<string, { screen: Screen; product: 'app' | 'pro' }> = {
  'pro.newsletter': { screen: 'marketing.newsletter', product: 'pro' },
  'app.newsletter': { screen: 'marketing.newsletter', product: 'app' },
  'pro.automation': { screen: 'marketing.automation', product: 'pro' },
  'app.automation': { screen: 'marketing.automation', product: 'app' },
}

export interface SubTab {
  key: Screen
  label: string
  /**
   * True when the screen is designed but its backend is deliberately not built
   * yet — the handoff withholds social-channel integration and email sending
   * pending a product-owner decision. The UI still exists; it says so.
   */
  pending?: boolean
  /**
   * Hidden from the tab row for anyone who is not an admin.
   *
   * A hint, not a boundary. The URL still resolves and the screen still renders
   * — it says who may see the data instead of showing it — and the data itself
   * is protected by `firestore.rules`, which is the only place that counts.
   */
  adminOnly?: boolean
}

export interface DestinationDef {
  key: Destination
  label: string
  tabs: SubTab[]
}

export const DESTINATIONS: DestinationDef[] = [
  {
    key: 'pro',
    label: 'Skida PRO',
    tabs: [
      { key: 'pro.dashboard', label: 'Dashboard' },
      { key: 'pro.crm', label: 'Sales CRM' },
    ],
  },
  {
    key: 'app',
    label: 'Skida App',
    tabs: [
      { key: 'app.dashboard', label: 'Dashboard' },
    ],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    tabs: [
      { key: 'marketing.plan', label: 'Publishing plan' },
      { key: 'marketing.stats', label: 'Channel stats' },
      { key: 'marketing.competitors', label: 'Competitors' },
      { key: 'marketing.events', label: 'Events' },
      { key: 'marketing.ambassadors', label: 'Ambassadors' },
      { key: 'marketing.partners', label: 'Partners' },
      { key: 'marketing.newsletter', label: 'Newsletter' },
      { key: 'marketing.automation', label: 'Email automation' },
    ],
  },
  {
    key: 'internal',
    label: 'Internal',
    tabs: [
      { key: 'internal.team', label: 'Team' },
      { key: 'internal.ownership', label: 'Ownership', adminOnly: true },
    ],
  },
]

/**
 * Skida PRO is the landing destination, and its dashboard the landing screen.
 *
 * Decision 62: the dashboard is the read you open with, the CRM is where you go
 * to do something. The mobile sheet needs no separate change — it renders the
 * same array, so reordering it above reorders both.
 */
export const DEFAULT_SCREEN: Screen = 'pro.dashboard'

/** Which destination a screen belongs to. `admin` belongs to none. */
export function destinationOf(screen: Screen): Destination | null {
  const found = DESTINATIONS.find((d) => d.tabs.some((t) => t.key === screen))
  return found?.key ?? null
}

/** The first screen of a destination — where clicking its nav pill lands. */
export function firstScreenOf(destination: Destination): Screen {
  return DESTINATIONS.find((d) => d.key === destination)!.tabs[0]!.key
}

export function tabsOf(destination: Destination): SubTab[] {
  return DESTINATIONS.find((d) => d.key === destination)?.tabs ?? []
}
